<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

import { ParentModel } from '@/types';
import MediaSelector from './MediaSelector.vue';

const props = defineProps({
  parent: {
    type: Object,
    required: true,
  },
  imgWidth: {
    type: Number,
    default: 350,
  },
  imgHeight: {
    type: Number,
    default: 350,
  },
  requiredWidth: {
    type: Number,
    default: null,
  },
  requiredHeight: {
    type: Number,
    default: null,
  },
  defaultTab: {
    type: Number,
    default: 2,
  },
  autoOpen: {
    type: Boolean,
    default: false,
  },
})

const mediaId = ref<number | null>(null)
const url = ref<string>('')
const alt = ref<string>('')
const title = ref<string>('')
const mediaWidth = ref<number | null>(null)
const mediaHeight = ref<number | null>(null)
const showImage = ref<boolean>(false)
const loading = ref<boolean>(false)
const openDialog = ref<boolean>(false)
const isError = ref<boolean>(false)
const parent = ref<ParentModel>(props.parent)

const hasRequiredDimensions = computed(() => props.requiredWidth && props.requiredHeight)

const dimensionMismatch = computed(() => {
  if (!hasRequiredDimensions.value || !mediaWidth.value || !mediaHeight.value) return false
  return Math.abs(mediaWidth.value - props.requiredWidth!) > 10 || Math.abs(mediaHeight.value - props.requiredHeight!) > 10
})

const imageSrc = computed(() => {
  if (!url.value) return ''
  if (hasRequiredDimensions.value) {
    return `${url.value}?auto=compress`
  }
  return `${url.value}?fit=crop&w=${props.imgWidth}&h=${props.imgHeight}&auto=compress`
})

const containerStyle = computed(() => {
  if (hasRequiredDimensions.value) {
    return { aspectRatio: `${props.requiredWidth} / ${props.requiredHeight}` }
  }
  return {}
})

onMounted(async () => {
  await loadImage()
  if (props.autoOpen && !showImage.value) {
    openDialog.value = true
  }
})

const loadImage = async () => {
  loading.value = true
  isError.value = false

  try {
    const response = await axios.get(`/api/${parent.value.type}/${parent.value.id}/image/${parent.value.channel}`)
    updateImage(response.data)
  } catch (error) {
    isError.value = true
    console.error(error)
  } finally {
    loading.value = false
  }
}

const updateImage = (response: any) => {
  if (response?.media?.id) {
    mediaId.value = response.media.id
    url.value = response.media.url
    alt.value = response.media.data.alt
    title.value = response.media.data.title
    mediaWidth.value = response.media.data.width ?? null
    mediaHeight.value = response.media.data.height ?? null
    showImage.value = true
  } else {
    mediaId.value = null
    url.value = ''
    alt.value = ''
    title.value = ''
    mediaWidth.value = null
    mediaHeight.value = null
    showImage.value = false
  }
}

const removeAttachment = async () => {
  loading.value = true
  isError.value = false

  try {
    await axios.delete(`/api/${parent.value.type}/${parent.value.id}/image/${parent.value.channel}`)
    showImage.value = false
  } catch (error) {
    isError.value = true
    console.error(error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="border border-base-200 bg-base-200 rounded-xl">
    <div v-if="showImage" class="relative">
      <!-- Dimension mismatch warning -->
      <div v-if="dimensionMismatch" class="absolute top-0 left-0 right-0 z-10 p-2">
        <div role="alert" class="alert alert-warning py-1 px-3 text-xs">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-4 w-4" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <span>Bild hat {{ mediaWidth }}&times;{{ mediaHeight }} px, erwartet: ca. {{ requiredWidth }}&times;{{ requiredHeight }} px (&plusmn;10 px)</span>
        </div>
      </div>
      <div :style="containerStyle" class="overflow-hidden">
        <img class="mx-auto w-full h-full object-contain" :alt="alt" :title="title" :src="imageSrc">
      </div>
      <button class="btn btn-square btn-error absolute bottom-0 left-0 mb-2 ml-2" @click.prevent="removeAttachment">
        <svg class="h-7 w-7 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><title>delete</title><path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" /></svg>
      </button>
      <button class="btn btn-square btn-info absolute bottom-0 right-0 mb-2 mr-2" @click.prevent="() => openDialog = true">
        <svg class="h-7 w-7 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><title>pencil</title><path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" /></svg>
      </button>
    </div>
    <div v-else-if="loading" class="flex items-center justify-center h-64">
      <span class="loading loading-spinner loading-md"></span>
    </div>
    <div v-else-if="isError" class="flex items-center justify-center h-64">
      <div role="alert" class="alert alert-error">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>Es ist ein Fehler beim Laden des Bildes aufgetreten! Bitte laden Sie die Seite neu!</span>
      </div>
    </div>
    <div v-else class="text-center">
      <button class="btn btn-wide w-full" @click.prevent="() => openDialog = true">Bild hinzufügen</button>
      <p v-if="hasRequiredDimensions" class="text-xs text-gray-400 mt-1">Benötigte Auflösung: {{ requiredWidth }} &times; {{ requiredHeight }} px</p>
    </div>
  </div>

  <MediaSelector v-model:openDialog="openDialog" v-model:selectedMedia="mediaId" :parent="parent" :default-tab="defaultTab" @update="(response: any) => updateImage(response)"/>

  <dialog id="removeAttachmentModal" class="modal">
    <div class="modal-box">
      <h3 class="font-bold text-lg">Möchten Sie die Verknüpfung entfernen?</h3>
      <p class="py-4">Das Bild wird nicht gelöscht, sondern lediglich die Verknüpfung zwischen dem Bild und dieser Ressource entfernt.</p>
      <div class="modal-action">
        <button type="submit" class="btn btn-error">Verknüpfung entfernen</button>
        <form method="dialog">
          <button class="btn">Abbrechen</button>
        </form>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button>close</button>
    </form>
  </dialog>
</template>
